import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getEventLogs, getUTMStats } from '@/lib/api';

export default function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [utmStats, setUtmStats] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    fetchRows();
    loadUTMStats();
  }, [page]);

  const fetchRows = async () => {
    setBusy(true);
    try {
      const res = await getEventLogs(page, 20);
      setLogs(res.data.logs);
      setTotalPages(res.data.pages);
    } catch (err) {
      toast.error('Не удалось загрузить логи');
    } finally {
      setBusy(false);
    }
  };

  const loadUTMStats = async () => {
    try {
      const res = await getUTMStats();
      setUtmStats(res.data);
    } catch (err) {
      console.error('Failed to load UTM stats');
    }
  };

  const getEventBadge = (type) => {
    switch (type) {
      case 'pageview':
        return <Badge variant="secondary">Визит</Badge>;
      case 'click_telegram':
        return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Telegram</Badge>;
      case 'click_whatsapp':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">WhatsApp</Badge>;
      case 'lead':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Лид</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const toShortDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-logs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Логи событий</h1>
          <p className="text-slate-500">История всех событий на сайте</p>
        </div>
        <Button variant="outline" onClick={fetchRows} data-testid="refresh-logs-button">
          <RefreshCw className="w-4 h-4 mr-2" />
          Обновить
        </Button>
      </div>

      {/* UTM Stats */}
      {utmStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>UTM источники</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {utmStats.slice(0, 6).map((item, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{item.source || 'direct'}</p>
                      <p className="text-xs text-slate-500">
                        {item.medium && `${item.medium} • `}
                        {item.campaign || 'без кампании'}
                      </p>
                    </div>
                    <span className="text-lg font-bold text-slate-900">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logs Table */}
      <Card>
        <CardContent className="pt-6">
          {busy ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Нет событий
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Время</TableHead>
                    <TableHead>Событие</TableHead>
                    <TableHead>Страна</TableHead>
                    <TableHead>Устройство</TableHead>
                    <TableHead>UTM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                      <TableCell className="text-sm text-slate-500">
                        {toShortDateTime(log.timestamp)}
                      </TableCell>
                      <TableCell>{getEventBadge(log.event_type)}</TableCell>
                      <TableCell className="text-sm">
                        {log.country || '-'}
                        {log.city && log.city !== 'Unknown' && (
                          <span className="text-slate-400">, {log.city}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {log.device || '-'}
                        {log.browser && <span className="text-slate-400"> / {log.browser}</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.utm_source ? (
                          <span className="text-slate-600">
                            {log.utm_source}
                            {log.utm_medium && ` / ${log.utm_medium}`}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Страница {page} из {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    data-testid="prev-page-button"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    data-testid="next-page-button"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
